/**
 * LLM Wrapper for Analyzer Nodes
 *
 * Provides unified interface for calling LLM clients in IAB classification workflow.
 * Handles retries, error handling, and response parsing.
 *
 * Sprint 2: Simplified to use @ownyou/llm-client providers
 *
 * @see docs/sprints/ownyou-sprint2-spec.md
 */
import { LLMProviderType, OpenAIProvider, AnthropicProvider, GoogleProvider, GroqProvider, DeepInfraProvider, OllamaProvider, calculateModelCost, } from '@ownyou/llm-client/providers';
const NON_RETRYABLE_ROUTE_ERROR_CODES = new Set([
    'missing_selection',
    'invalid_selection',
    'unavailable_selection',
    'unsupported_provider',
    'missing_auth',
]);
function getRouteErrorCode(error) {
    if (typeof error !== 'object' || error === null || !('code' in error)) {
        return null;
    }
    const code = error.code;
    return typeof code === 'string' ? code : null;
}
function isNonRetryableRouteError(error) {
    const code = getRouteErrorCode(error);
    if (code && NON_RETRYABLE_ROUTE_ERROR_CODES.has(code))
        return true;
    const message = error instanceof Error ? error.message : String(error);
    return message.includes('unavailable_route:');
}
/**
 * Map provider name to LLMProviderType
 */
const PROVIDER_MAP = {
    openai: LLMProviderType.OPENAI,
    claude: LLMProviderType.ANTHROPIC,
    anthropic: LLMProviderType.ANTHROPIC,
    google: LLMProviderType.GOOGLE,
    gemini: LLMProviderType.GOOGLE,
    groq: LLMProviderType.GROQ,
    deepinfra: LLMProviderType.DEEPINFRA,
    xai: LLMProviderType.OPENAI,
    grok: LLMProviderType.OPENAI,
    ollama: LLMProviderType.OLLAMA,
};
/**
 * Default models per provider (fallback when no model specified)
 *
 * NOTE: These are FALLBACK defaults only, used when user doesn't specify a model.
 * The model SELECTION UI should fetch available models from provider APIs.
 * See: apps/consumer/src/utils/fetch-models.ts
 */
const DEFAULT_MODELS = {
    openai: 'gpt-4o-mini',
    claude: 'claude-3-haiku-20240307',
    anthropic: 'claude-3-haiku-20240307',
    google: 'gemini-2.0-flash',
    gemini: 'gemini-2.0-flash',
    groq: 'llama-3.1-8b-instant',
    deepinfra: 'meta-llama/Llama-3.3-70B-Instruct',
    xai: 'grok-3-mini',
    grok: 'grok-3-mini',
    ollama: 'llama3.2',
};
/**
 * Normalize model ID - SPRINT 11g: Rosetta Stone eliminates aliases
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  SPRINT 11g: MODEL_ID_ALIASES DELETED                                       ║
 * ║                                                                              ║
 * ║  The Rosetta Stone pattern fetches REAL model IDs from provider APIs.       ║
 * ║  Model ID aliasing is no longer needed - we use the actual IDs.             ║
 * ║                                                                              ║
 * ║  If you're seeing 404 errors, ensure you're using ConfigService:            ║
 * ║    const models = await configService.refreshModels(apiKeys);               ║
 * ║    // models[].id contains the REAL provider IDs                            ║
 * ║                                                                              ║
 * ║  @see docs/sprints/ownyou-sprint11g-spec.md                                 ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 *
 * @param model - Model ID (should be real provider ID from Rosetta Stone)
 * @param provider - Target provider name (unused in Sprint 11g+)
 * @returns The same model ID (no aliasing needed with Rosetta Stone)
 */
function normalizeModelId(model, _provider) {
    // Sprint 11g: With Rosetta Stone, model IDs come directly from provider APIs
    // No aliasing needed - just return the model as-is
    return model;
}
/**
 * Unified LLM client for analyzer nodes with retry logic.
 *
 * Sprint 2: Now uses @ownyou/llm-client providers internally.
 */
export class AnalyzerLLMClient {
    provider;
    max_retries;
    cost_tracker;
    workflow_tracker;
    model;
    llm_config;
    _provider;
    /**
     * Initialize LLM client for analyzer use.
     *
     * @param provider - "openai", "claude", "ollama", "groq", "deepinfra", or "google"
     * @param model - Specific model name (optional, uses default from client)
     * @param max_retries - Number of retry attempts on failure (default: 3)
     * @param cost_tracker - Optional CostTracker instance to track API costs
     * @param workflow_tracker - Optional WorkflowTracker instance for dashboard analytics
     * @param llm_config - Optional LLM configuration (API keys, etc.)
     */
    constructor(params = {}) {
        // Parse model spec if it contains provider (format: "provider:model")
        // Only parse if provider is NOT already set - otherwise model may contain colons
        // (e.g., Ollama models use "model:tag" format like "qwen3:8b")
        let provider = params.provider;
        let model = params.model;
        if (model && model.includes(':') && (provider === null || provider === undefined)) {
            const [provider_from_model, ...model_parts] = model.split(':');
            provider = provider_from_model;
            model = model_parts.join(':'); // Rejoin in case model has multiple colons
            console.debug(`Parsed model spec: provider=${provider}, model=${model}`);
        }
        // Default to LLM_PROVIDER from environment, or "openai" as fallback
        if (provider === null || provider === undefined) {
            provider = globalThis.process?.env?.LLM_PROVIDER || 'openai';
        }
        this.provider = provider.toLowerCase();
        this.max_retries = params.max_retries ?? 3;
        this.cost_tracker = params.cost_tracker ?? null;
        this.workflow_tracker = params.workflow_tracker ?? null;
        this.llm_config = params.llm_config ?? null;
        // Use provided model or fallback to default for provider
        this.model = model || DEFAULT_MODELS[this.provider] || 'gpt-4o-mini';
        // Initialize provider from @ownyou/llm-client
        this._provider = this._createProvider();
        console.info(`Initialized ${this.provider} LLM client for analyzers (model: ${this.model})`);
    }
    /**
     * Get environment variable from Vite or Node.
     * In browser (Vite), uses import.meta.env.VITE_*
     * In Node, uses process.env.*
     */
    _getEnvVar(key) {
        // Check llm_config first (passed explicitly)
        if (this.llm_config?.api_key) {
            return this.llm_config.api_key;
        }
        // Try Vite environment (import.meta.env)
        if (typeof import.meta !== 'undefined' && import.meta.env) {
            const viteKey = key.startsWith('VITE_') ? key : `VITE_${key}`;
            const value = import.meta.env[viteKey];
            if (value)
                return value;
        }
        // Try Node environment (process.env) via globalThis
        const proc = globalThis.process;
        if (proc && proc.env) {
            const value = proc.env[key];
            if (value)
                return value;
        }
        return '';
    }
    /**
     * Create the appropriate LLM provider instance.
     */
    _createProvider() {
        const providerType = PROVIDER_MAP[this.provider];
        if (!providerType) {
            throw new Error(`Unknown provider: ${this.provider}. Must be 'claude', 'openai', 'ollama', 'groq', 'deepinfra', 'xai', or 'google'`);
        }
        // Normalize model ID for provider-specific API format
        const normalizedModel = normalizeModelId(this.model, this.provider);
        if (normalizedModel !== this.model) {
            console.info(`[AnalyzerLLMClient] Model ID normalized: ${this.model} → ${normalizedModel}`);
            this.model = normalizedModel;
        }
        // Log API key status for debugging
        const hasApiKey = !!(this.llm_config?.api_key);
        console.debug(`[AnalyzerLLMClient] Creating ${this.provider} provider, model=${this.model}, hasApiKey=${hasApiKey}`);
        switch (providerType) {
            case LLMProviderType.OPENAI:
                return new OpenAIProvider({
                    apiKey: this.llm_config?.api_key || this._getEnvVar('OPENAI_API_KEY'),
                    baseUrl: this.llm_config?.base_url,
                    model: this.model,
                });
            case LLMProviderType.ANTHROPIC:
                return new AnthropicProvider({
                    apiKey: this.llm_config?.api_key || this._getEnvVar('ANTHROPIC_API_KEY'),
                    baseUrl: this.llm_config?.base_url,
                    model: this.model,
                });
            case LLMProviderType.GOOGLE:
                return new GoogleProvider({
                    apiKey: this.llm_config?.api_key || this._getEnvVar('GOOGLE_API_KEY'),
                    model: this.model,
                });
            case LLMProviderType.GROQ:
                return new GroqProvider({
                    apiKey: this.llm_config?.api_key || this._getEnvVar('GROQ_API_KEY'),
                    baseUrl: this.llm_config?.base_url,
                    model: this.model,
                });
            case LLMProviderType.DEEPINFRA:
                return new DeepInfraProvider({
                    apiKey: this.llm_config?.api_key || this._getEnvVar('DEEPINFRA_API_KEY'),
                    baseUrl: this.llm_config?.base_url,
                    model: this.model,
                });
            case LLMProviderType.OLLAMA:
                return new OllamaProvider({
                    baseUrl: this.llm_config?.base_url || this._getEnvVar('OLLAMA_HOST') || 'http://localhost:11434',
                    model: this.model,
                });
            default:
                throw new Error(`Provider ${this.provider} not implemented`);
        }
    }
    /**
     * Analyze email using LLM with retry logic.
     *
     * @param prompt - Complete prompt with email content and instructions
     * @param max_tokens - Maximum response tokens (default: 1000)
     * @param temperature - Sampling temperature, lower = more deterministic (default: 0.1)
     * @returns Parsed JSON response with classifications array
     */
    async analyze_email(params) {
        const { prompt, max_tokens = null, temperature = 0.1 } = params;
        console.info(`analyze_email: max_tokens=${max_tokens} (will be auto-adjusted by client)`);
        console.debug('[DEBUG] Prompt length:', prompt.length, 'characters');
        const request = {
            messages: [{ role: 'user', content: prompt }],
            model: this.model,
            maxTokens: max_tokens ?? undefined,
            temperature,
        };
        for (let attempt = 1; attempt <= this.max_retries; attempt++) {
            try {
                console.debug(`LLM call attempt ${attempt}/${this.max_retries}`);
                // Call LLM via @ownyou/llm-client provider
                const response = await this._provider.complete(request);
                if (response.error) {
                    throw new Error(`LLM call failed: ${response.error}`);
                }
                // Parse JSON response without logging model output content.
                const result = this._parseJsonResponse(response.content);
                console.debug('[DEBUG] Parsed LLM response JSON');
                // Validate structure
                if (typeof result !== 'object' || result === null || Array.isArray(result)) {
                    throw new Error('Response is not a JSON object');
                }
                if (!('classifications' in result)) {
                    console.warn('Response missing "classifications" key, adding empty array');
                    result.classifications = [];
                }
                // Track costs if tracker provided
                // DEBUG: Log cost tracking attempt
                console.log(`[CostTrack] cost_tracker present: ${!!this.cost_tracker}, response.usage: ${JSON.stringify(response.usage)}`);
                if (this.cost_tracker && response.usage) {
                    const prompt_tokens = response.usage.inputTokens || 0;
                    const completion_tokens = response.usage.outputTokens || 0;
                    console.log(`[CostTrack] prompt_tokens: ${prompt_tokens}, completion_tokens: ${completion_tokens}`);
                    if (prompt_tokens > 0 || completion_tokens > 0) {
                        const cost = this.cost_tracker.track_call({
                            provider: this.provider,
                            model: this.model,
                            prompt_tokens,
                            completion_tokens,
                        });
                        console.log(`[CostTrack] Tracked LLM cost: $${cost.toFixed(6)} USD for ${this.model}`);
                        if (this.workflow_tracker) {
                            this.workflow_tracker.record_cost({
                                provider: this.provider,
                                cost,
                                model_name: this.model,
                                input_tokens: prompt_tokens,
                                output_tokens: completion_tokens,
                            });
                        }
                    }
                }
                else {
                    console.log(`[CostTrack] SKIPPED: cost_tracker=${!!this.cost_tracker}, response.usage=${!!response.usage}`);
                }
                console.info('LLM call successful', {
                    provider: this.provider,
                    attempt,
                    tokens: response.usage.totalTokens || 0,
                    classifications: (result.classifications || []).length,
                });
                return result;
            }
            catch (error) {
                if (error instanceof SyntaxError || error.name === 'SyntaxError') {
                    console.warn(`JSON parse error (attempt ${attempt}/${this.max_retries}): ${error}`);
                    if (attempt < this.max_retries) {
                        await this._sleep(2 ** (attempt - 1) * 1000);
                        continue;
                    }
                    else {
                        console.error('All retry attempts failed due to JSON parsing errors');
                        return { classifications: [] };
                    }
                }
                console.error(`LLM call error (attempt ${attempt}/${this.max_retries}): ${error}`, error);
                if (attempt < this.max_retries) {
                    await this._sleep(2 ** (attempt - 1) * 1000);
                    continue;
                }
                else {
                    console.error('All retry attempts exhausted');
                    throw error;
                }
            }
        }
        return { classifications: [] };
    }
    /**
     * Call LLM and parse JSON response without enforcing specific structure.
     *
     * This is useful for LLM-as-Judge and other tasks that don't return
     * classifications.
     *
     * @param prompt - Complete prompt
     * @param max_tokens - Maximum response tokens
     * @param temperature - Sampling temperature (default: 0.1)
     * @returns Parsed JSON response as-is
     */
    async call_json(params) {
        const { prompt, temperature = 0.1 } = params;
        let { max_tokens = null } = params;
        if (max_tokens === null || max_tokens === undefined) {
            // Use 4096 as safe default for evidence judge calls
            // Claude 3.5 models have 8192 max, but we don't need that much for JSON responses
            max_tokens = 4096;
            console.debug(`call_json: max_tokens=null, using safe default of ${max_tokens}`);
        }
        const request = {
            messages: [{ role: 'user', content: prompt }],
            model: this.model,
            maxTokens: max_tokens,
            temperature,
        };
        for (let attempt = 1; attempt <= this.max_retries; attempt++) {
            try {
                console.debug(`LLM call_json attempt ${attempt}/${this.max_retries}`);
                const response = await this._provider.complete(request);
                if (response.error) {
                    throw new Error(`LLM call failed: ${response.error}`);
                }
                const result = this._parseJsonResponse(response.content);
                if (typeof result !== 'object' || result === null || Array.isArray(result)) {
                    throw new Error('Response is not a JSON object');
                }
                // Track costs
                if (this.cost_tracker && response.usage) {
                    const prompt_tokens = response.usage.inputTokens || 0;
                    const completion_tokens = response.usage.outputTokens || 0;
                    if (prompt_tokens > 0 || completion_tokens > 0) {
                        const cost = this.cost_tracker.track_call({
                            provider: this.provider,
                            model: this.model,
                            prompt_tokens,
                            completion_tokens,
                        });
                        console.debug(`Tracked LLM cost: $${cost.toFixed(6)} USD`);
                        if (this.workflow_tracker) {
                            this.workflow_tracker.record_cost({
                                provider: this.provider,
                                cost,
                                model_name: this.model,
                                input_tokens: prompt_tokens,
                                output_tokens: completion_tokens,
                            });
                        }
                    }
                }
                console.debug(`call_json successful (keys: ${Object.keys(result).join(', ')})`);
                return result;
            }
            catch (error) {
                if (error instanceof SyntaxError || error.name === 'SyntaxError') {
                    console.warn(`JSON parse error (attempt ${attempt}/${this.max_retries}): ${error}`);
                    if (attempt < this.max_retries) {
                        await this._sleep(2 ** (attempt - 1) * 1000);
                        continue;
                    }
                    else {
                        console.error('All retry attempts failed due to JSON parsing errors');
                        throw error;
                    }
                }
                console.error(`call_json error (attempt ${attempt}/${this.max_retries}): ${error}`, error);
                if (attempt < this.max_retries) {
                    await this._sleep(2 ** (attempt - 1) * 1000);
                    continue;
                }
                else {
                    console.error('All retry attempts exhausted');
                    throw error;
                }
            }
        }
        throw new Error('call_json: max retries exceeded');
    }
    /**
     * Parse JSON from LLM response, handling common formatting issues.
     */
    _parseJsonResponse(content) {
        return parseLLMJsonResponse(content);
    }
    /**
     * Estimate cost of LLM call using @ownyou/llm-client pricing.
     */
    estimate_cost(params) {
        const { prompt_tokens, response_tokens } = params;
        try {
            return calculateModelCost(this.model, prompt_tokens, response_tokens);
        }
        catch {
            // Fallback pricing for unknown models
            const fallbackPricing = {
                claude: { prompt: 3.0 / 1_000_000, response: 15.0 / 1_000_000 },
                openai: { prompt: 0.15 / 1_000_000, response: 0.6 / 1_000_000 },
                ollama: { prompt: 0.0, response: 0.0 },
                groq: { prompt: 0.59 / 1_000_000, response: 0.79 / 1_000_000 },
                deepinfra: { prompt: 0.35 / 1_000_000, response: 0.4 / 1_000_000 },
                google: { prompt: 0.075 / 1_000_000, response: 0.3 / 1_000_000 },
            };
            if (!(this.provider in fallbackPricing)) {
                return null;
            }
            const rates = fallbackPricing[this.provider];
            return prompt_tokens * rates.prompt + response_tokens * rates.response;
        }
    }
    /**
     * Sleep for specified milliseconds (utility for exponential backoff).
     */
    _sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}
export class RouteBackedAnalyzerLLMClient {
    provider;
    model;
    max_retries;
    cost_tracker;
    workflow_tracker;
    routeId;
    executeRoute;
    constructor(params) {
        this.routeId = params.routeId;
        this.provider = params.provider;
        this.model = params.model;
        this.executeRoute = params.executeRoute;
        this.max_retries = params.max_retries ?? 3;
        this.cost_tracker = params.cost_tracker ?? null;
        this.workflow_tracker = params.workflow_tracker ?? null;
    }
    async analyze_email(params) {
        const result = await this.callRouteJson({
            prompt: params.prompt,
            max_tokens: params.max_tokens,
            temperature: params.temperature ?? 0.1,
        });
        if (!('classifications' in result)) {
            console.warn('Response missing "classifications" key, adding empty array');
            result.classifications = [];
        }
        return result;
    }
    async call_json(params) {
        return this.callRouteJson({
            prompt: params.prompt,
            max_tokens: params.max_tokens ?? 4096,
            temperature: params.temperature ?? 0.1,
        });
    }
    async callRouteJson(params) {
        let lastError;
        for (let attempt = 1; attempt <= this.max_retries; attempt++) {
            try {
                const response = await this.executeRoute({
                    routeId: this.routeId,
                    messages: [{ role: 'user', content: params.prompt }],
                    maxTokens: params.max_tokens ?? undefined,
                    temperature: params.temperature,
                });
                this.trackUsage(response.usage, response.provider, response.model);
                const parsed = parseLLMJsonResponse(response.content);
                if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                    throw new Error('Response is not a JSON object');
                }
                return parsed;
            }
            catch (error) {
                lastError = error;
                if (isNonRetryableRouteError(error)) {
                    throw error instanceof Error ? error : new Error(String(error));
                }
                if (attempt < this.max_retries) {
                    await new Promise((resolve) => setTimeout(resolve, 2 ** (attempt - 1) * 1000));
                    continue;
                }
            }
        }
        throw lastError instanceof Error ? lastError : new Error(String(lastError));
    }
    trackUsage(usage, provider, model) {
        if (!this.cost_tracker || !usage)
            return;
        const prompt_tokens = usage.inputTokens || 0;
        const completion_tokens = usage.outputTokens || 0;
        if (prompt_tokens <= 0 && completion_tokens <= 0)
            return;
        const cost = this.cost_tracker.track_call({
            provider,
            model,
            prompt_tokens,
            completion_tokens,
        });
        this.workflow_tracker?.record_cost({
            provider,
            cost,
            model_name: model,
            input_tokens: prompt_tokens,
            output_tokens: completion_tokens,
        });
    }
}
export function parseLLMJsonResponse(content) {
    let json = content.trim();
    if (json.startsWith('```json')) {
        json = json.slice(7);
    }
    if (json.startsWith('```')) {
        json = json.slice(3);
    }
    if (json.endsWith('```')) {
        json = json.slice(0, -3);
    }
    json = json.trim();
    try {
        return JSON.parse(json);
    }
    catch (error) {
        const start = json.indexOf('{');
        const end = json.lastIndexOf('}');
        if (start !== -1 && end !== -1) {
            json = json.slice(start, end + 1);
            return JSON.parse(json);
        }
        throw error;
    }
}
export default AnalyzerLLMClient;
//# sourceMappingURL=client.js.map